import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { Outlet, useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { fetchWithAuth } from '../utils/fetchWithAuth';
import { API_BASE_URL } from '../config';
import { usePermissions } from '../context/PermissionsContext';
import { PERMISSIONS } from '../constants/permissions';
import { formatQuotationId } from '../utils/displayIds';
import LoadingSpinner from './LoadingSpinner';
import CarePlanDetail from './CarePlanDetail';
import QuotationDetail from './QuotationDetails';
import LabourDifferenceSummary, { emptyHourTotals } from './LabourDifferenceSummary';
import './PlanQuoteDetail.css';

const C = {
    dark: '#131926',
    textMuted: 'var(--theme-text-muted)',
    border: 'var(--theme-border)',
    accent: 'var(--theme-accent)',
};

const sessionKey = (carePlanId) => `sp-quote-selection:${carePlanId}`;

function readRememberedQuote(carePlanId) {
    try {
        return sessionStorage.getItem(sessionKey(carePlanId)) || null;
    } catch {
        return null;
    }
}

function rememberQuote(carePlanId, quotationId) {
    try {
        sessionStorage.setItem(sessionKey(carePlanId), String(quotationId));
    } catch {
        // ignore storage failures
    }
}

function quoteStatusLabel(status) {
    const n = Number(status);
    if (n === 1) return 'Active';
    if (n === 3) return 'Archived';
    if (n === 2) return 'Removed';
    return 'Draft';
}

function ServicePlanQuoteShell() {
    const { id, carePlanId, Qid: urlQid } = useParams();
    const navigate = useNavigate();
    const { collapsed: sidebarCollapsed } = useOutletContext() || {};
    const { hasPermission } = usePermissions();
    const canReadQuotes = hasPermission(PERMISSIONS.QUOTATION_READ);

    const [quotes, setQuotes] = useState([]);
    const [quotesLoaded, setQuotesLoaded] = useState(false);
    const [selectedQuotationId, setSelectedQuotationId] = useState(urlQid || null);
    const [showPicker, setShowPicker] = useState(false);
    const [pickerPurpose, setPickerPurpose] = useState('select'); // select | change
    const [quotePanelMounted, setQuotePanelMounted] = useState(Boolean(urlQid));
    const [planEditState, setPlanEditState] = useState({ editing: false, dirty: false });
    const [quoteEditState, setQuoteEditState] = useState({ editing: false, dirty: false });
    // Bumped when the service plan is saved so the mounted quote panel refetches labour totals
    const [planRevision, setPlanRevision] = useState(0);
    const [planHourTotals, setPlanHourTotals] = useState(emptyHourTotals);
    const [quoteHourTotals, setQuoteHourTotals] = useState(emptyHourTotals);
    const [isStickyDiff, setIsStickyDiff] = useState(false);
    const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth <= 768 : false);

    const activeTab = urlQid ? 'quote' : 'plan';

    const selectableQuotes = useMemo(
        () => (quotes || []).filter((q) => Number(q.status) !== 2),
        [quotes]
    );

    const selectedQuote = useMemo(
        () => selectableQuotes.find((q) => String(q.quotation_id) === String(selectedQuotationId)) || null,
        [selectableQuotes, selectedQuotationId]
    );

    const handleHourTotalsChange = useCallback((source, hours) => {
        if (source === 'plan') {
            setPlanHourTotals(hours || emptyHourTotals());
        } else if (source === 'quote') {
            setQuoteHourTotals(hours || emptyHourTotals());
        }
    }, []);

    const fetchQuotes = useCallback(async () => {
        if (!id || !carePlanId || !canReadQuotes) {
            setQuotes([]);
            setQuotesLoaded(true);
            return [];
        }
        try {
            const response = await fetchWithAuth(`${API_BASE_URL}/quotes/${id}/${carePlanId}`);
            if (response.status === 404) {
                setQuotes([]);
                setQuotesLoaded(true);
                return [];
            }
            if (!response.ok) {
                console.error('Failed to load quotes for service plan', response.status);
                setQuotes([]);
                setQuotesLoaded(true);
                return [];
            }
            const data = await response.json();
            const list = (data.quotations || []).filter((q) => Number(q.status) !== 2);
            setQuotes(list);
            setQuotesLoaded(true);
            return list;
        } catch (err) {
            console.error('Error loading quotes for service plan', err);
            setQuotes([]);
            setQuotesLoaded(true);
            return [];
        }
    }, [id, carePlanId, canReadQuotes]);

    // Reload quote list when the service plan changes
    useEffect(() => {
        setQuotesLoaded(false);
        setSelectedQuotationId(urlQid || null);
        setQuotePanelMounted(Boolean(urlQid));
        setPlanHourTotals(emptyHourTotals());
        setQuoteHourTotals(emptyHourTotals());
        setPlanRevision(0);
        fetchQuotes();
        // Only reset when the plan identity / fetch fn changes — urlQid handled below
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchQuotes]);

    // Keep selection in sync with the quote URL segment
    useEffect(() => {
        if (!urlQid) return;
        setSelectedQuotationId(urlQid);
        rememberQuote(carePlanId, urlQid);
        setQuotePanelMounted(true);
    }, [urlQid, carePlanId]);

    // When on the plan tab, auto-select single quote or restore session memory
    useEffect(() => {
        if (!quotesLoaded || urlQid) return;

        if (selectableQuotes.length === 1) {
            const onlyId = String(selectableQuotes[0].quotation_id);
            setSelectedQuotationId(onlyId);
            rememberQuote(carePlanId, onlyId);
            setQuotePanelMounted(true);
            return;
        }

        if (selectableQuotes.length > 1) {
            const remembered = readRememberedQuote(carePlanId);
            if (remembered && selectableQuotes.some((q) => String(q.quotation_id) === String(remembered))) {
                setSelectedQuotationId(remembered);
                setQuotePanelMounted(true);
            }
        }
    }, [quotesLoaded, selectableQuotes, urlQid, carePlanId]);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Sticky labour difference after scrolling past overview (same threshold as former quote bar)
    useEffect(() => {
        if (!selectedQuotationId) {
            setIsStickyDiff(false);
            return undefined;
        }
        const handleScroll = () => {
            setIsStickyDiff(window.scrollY > 800);
        };
        handleScroll();
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, [selectedQuotationId]);

    const confirmLeaveIfDirty = (leavingTab) => {
        const dirty = leavingTab === 'plan' ? planEditState.dirty : quoteEditState.dirty;
        if (!dirty) return true;
        return window.confirm('You have unsaved changes. Switch tabs anyway?');
    };

    const goToPlanTab = () => {
        if (activeTab === 'plan') return;
        if (!confirmLeaveIfDirty('quote')) return;
        navigate(`/careplans/${id}/${carePlanId}`, { replace: true });
    };

    const applyQuoteSelection = (quotationId, { replace = true } = {}) => {
        const qid = String(quotationId);
        setSelectedQuotationId(qid);
        rememberQuote(carePlanId, qid);
        setQuotePanelMounted(true);
        setShowPicker(false);
        navigate(`/careplans/${id}/${carePlanId}/quote/${qid}`, { replace });
    };

    const goToQuoteTab = () => {
        if (!canReadQuotes || selectableQuotes.length === 0) return;
        if (activeTab === 'quote' && selectedQuotationId) return;
        if (!confirmLeaveIfDirty('plan')) return;

        if (selectableQuotes.length === 1) {
            applyQuoteSelection(selectableQuotes[0].quotation_id);
            return;
        }

        if (selectedQuotationId && selectableQuotes.some((q) => String(q.quotation_id) === String(selectedQuotationId))) {
            applyQuoteSelection(selectedQuotationId);
            return;
        }

        const remembered = readRememberedQuote(carePlanId);
        if (remembered && selectableQuotes.some((q) => String(q.quotation_id) === String(remembered))) {
            applyQuoteSelection(remembered);
            return;
        }

        setPickerPurpose('select');
        setShowPicker(true);
    };

    const handleQuoteCreated = async (quotationId) => {
        const list = await fetchQuotes();
        const qid = String(quotationId);
        const exists = list.some((q) => String(q.quotation_id) === qid);
        if (!exists) {
            setQuotes((prev) => [
                { quotation_id: qid, date_created: new Date().toISOString().split('T')[0], status: 0 },
                ...prev,
            ]);
        }
        applyQuoteSelection(qid, { replace: false });
    };

    const handleOpenQuoteFromPlan = (quotationId) => {
        if (!quotationId) return;
        if (!confirmLeaveIfDirty('plan')) return;
        applyQuoteSelection(quotationId);
    };

    const handleChangeQuote = () => {
        if (selectableQuotes.length < 2) return;
        if (!confirmLeaveIfDirty('quote')) return;
        setPickerPurpose('change');
        setShowPicker(true);
    };

    const quoteTabDisabled = !canReadQuotes || !quotesLoaded || selectableQuotes.length === 0;
    const canGoToPlan = activeTab === 'quote';
    const canGoToQuote = activeTab === 'plan' && !quoteTabDisabled;

    const stickyLeft = isMobile ? 0 : (sidebarCollapsed ? 72 : 280);
    const stickyWidth = isMobile ? '100%' : `calc(100% - ${sidebarCollapsed ? 72 : 280}px)`;

    const tabBtn = (key, label, { disabled = false, count } = {}) => {
        const isActive = activeTab === key;
        return (
            <button
                type="button"
                disabled={disabled}
                onClick={() => (key === 'plan' ? goToPlanTab() : goToQuoteTab())}
                style={{
                    padding: '10px 22px',
                    background: 'none',
                    border: 'none',
                    borderBottom: isActive ? `2px solid ${C.accent}` : '2px solid transparent',
                    marginBottom: '-2px',
                    fontWeight: isActive ? 700 : 500,
                    color: disabled ? '#adb5bd' : isActive ? 'var(--theme-text)' : C.textMuted,
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    fontSize: '0.92rem',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 8,
                    opacity: disabled ? 0.65 : 1,
                    transition: 'color 0.15s',
                }}
                title={
                    disabled
                        ? (!canReadQuotes
                            ? 'You do not have permission to view quotations'
                            : 'Create a quotation from this service plan to enable this tab')
                        : undefined
                }
            >
                {label}
                {typeof count === 'number' && count > 0 && (
                    <span
                        style={{
                            background: isActive ? 'var(--theme-accent)' : '#9ca3af',
                            color: '#fff',
                            borderRadius: 10,
                            padding: '1px 7px',
                            fontSize: '0.72rem',
                            fontWeight: 700,
                            lineHeight: '1.5',
                        }}
                    >
                        {count}
                    </span>
                )}
            </button>
        );
    };

    if (!carePlanId) {
        return (
            <div className="container mt-4">
                <LoadingSpinner message="Loading..." />
            </div>
        );
    }

    return (
        <div>
            {selectedQuotationId && isStickyDiff && (
                <div
                    className="pqd-shell-sticky-diff"
                    style={{ left: stickyLeft, width: stickyWidth }}
                >
                    <LabourDifferenceSummary
                        compact
                        planHours={planHourTotals}
                        quoteHours={quoteHourTotals}
                    />
                </div>
            )}

            <div
                className="pqd-shell-side-nav pqd-shell-side-nav--left"
                style={{ left: isMobile ? 8 : stickyLeft + 10 }}
            >
                <button
                    type="button"
                    className={`pqd-shell-side-nav-btn${canGoToPlan ? ' is-active' : ''}`}
                    disabled={!canGoToPlan}
                    onClick={goToPlanTab}
                    title={canGoToPlan ? 'Go to Service Plan' : 'Already on Service Plan'}
                    aria-label="Go to Service Plan"
                >
                    <FaChevronLeft />
                    <span className="pqd-shell-side-nav-label">Plan</span>
                </button>
            </div>
            <div className="pqd-shell-side-nav pqd-shell-side-nav--right">
                <button
                    type="button"
                    className={`pqd-shell-side-nav-btn${canGoToQuote ? ' is-active' : ''}`}
                    disabled={!canGoToQuote}
                    onClick={goToQuoteTab}
                    title={
                        quoteTabDisabled
                            ? (!canReadQuotes
                                ? 'You do not have permission to view quotations'
                                : 'Create a quotation from this service plan to enable this tab')
                            : canGoToQuote
                                ? 'Go to Quote'
                                : 'Already on Quote'
                    }
                    aria-label="Go to Quote"
                >
                    <span className="pqd-shell-side-nav-label">Quote</span>
                    <FaChevronRight />
                </button>
            </div>

            <div className="pqd-shell-tabs">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', borderBottom: `2px solid ${C.border}`, flex: 1, minWidth: 240 }}>
                        {tabBtn('plan', 'Service Plan')}
                        {tabBtn('quote', 'Quote', {
                            disabled: quoteTabDisabled,
                            count: selectableQuotes.length,
                        })}
                    </div>
                    {activeTab === 'quote' && selectableQuotes.length > 1 && (
                        <Button variant="outline-secondary" size="sm" onClick={handleChangeQuote} style={{ marginBottom: 8 }}>
                            Change quote
                        </Button>
                    )}
                </div>
            </div>

            <div style={{ display: activeTab === 'plan' ? 'block' : 'none' }}>
                <CarePlanDetail
                    embedded
                    onQuoteCreated={handleQuoteCreated}
                    onEditStateChange={setPlanEditState}
                    onOpenQuote={handleOpenQuoteFromPlan}
                    onPlanSaved={() => setPlanRevision((n) => n + 1)}
                    onHourTotalsChange={handleHourTotalsChange}
                    showLabourDifference={Boolean(selectedQuotationId)}
                    quoteHourTotals={selectedQuotationId ? quoteHourTotals : null}
                />
            </div>

            {quotePanelMounted && selectedQuotationId && (
                <div style={{ display: activeTab === 'quote' ? 'block' : 'none' }}>
                    <QuotationDetail
                        key={selectedQuotationId}
                        embedded
                        quotationId={String(selectedQuotationId)}
                        customerId={id}
                        dateCreated={selectedQuote?.date_created}
                        onEditStateChange={setQuoteEditState}
                        onOpenPlan={goToPlanTab}
                        isActive={activeTab === 'quote'}
                        planRevision={planRevision}
                        onHourTotalsChange={handleHourTotalsChange}
                        planHourTotals={planHourTotals}
                    />
                </div>
            )}

            <Modal show={showPicker} onHide={() => setShowPicker(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>
                        {pickerPurpose === 'change' ? 'Change quotation' : 'Choose a quotation'}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p style={{ color: C.textMuted, fontSize: '0.9rem' }}>
                        This service plan has more than one quotation. Pick which one to open.
                    </p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {selectableQuotes.map((q) => {
                            const isSelected = String(q.quotation_id) === String(selectedQuotationId);
                            const isActive = Number(q.status) === 1;
                            return (
                                <button
                                    key={q.quotation_id}
                                    type="button"
                                    onClick={() => applyQuoteSelection(q.quotation_id)}
                                    style={{
                                        textAlign: 'left',
                                        padding: '12px 14px',
                                        borderRadius: 8,
                                        border: isSelected ? `2px solid ${C.dark}` : `1px solid ${C.border}`,
                                        background: isSelected ? 'var(--theme-hover)' : 'var(--theme-surface)',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        gap: 12,
                                    }}
                                >
                                    <div>
                                        <div style={{ fontWeight: 700 }}>
                                            {formatQuotationId(q.quotation_id)}
                                        </div>
                                        <div style={{ fontSize: '0.85rem', color: C.textMuted }}>
                                            Created {q.date_created || '—'}
                                        </div>
                                    </div>
                                    <span
                                        style={{
                                            display: 'inline-block',
                                            padding: '2px 10px',
                                            borderRadius: 12,
                                            fontSize: '0.8rem',
                                            fontWeight: 600,
                                            background: isActive ? '#d1e7dd' : '#e9ecef',
                                            color: isActive ? '#0f5132' : '#495057',
                                        }}
                                    >
                                        {quoteStatusLabel(q.status)}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowPicker(false)}>
                        Cancel
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Keep nested quote route matched without rendering alternate UI */}
            <Outlet />
        </div>
    );
}

export default ServicePlanQuoteShell;
